import { clientDb } from './db';

const subscribers = new Set<(count: number) => void>();
let currentCount = 0;
let syncInFlight = false;

async function refreshCount() {
  currentCount = await clientDb.mutations.where('status').anyOf('pending', 'failed', 'syncing').count();
  subscribers.forEach((subscriber) => subscriber(currentCount));
}

export function subscribePendingMutationCount(subscriber: (count: number) => void) {
  subscribers.add(subscriber);
  subscriber(currentCount);
  void refreshCount();
  return () => subscribers.delete(subscriber);
}

export async function pendingMutationCount(): Promise<number> {
  return clientDb.mutations.where('status').anyOf('pending', 'failed', 'syncing').count();
}

export async function queueMutation(input: {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url: string;
  body: unknown;
}): Promise<string> {
  const operationId = crypto.randomUUID();
  await clientDb.mutations.add({
    operationId,
    method: input.method,
    url: input.url,
    body: input.body,
    createdAt: new Date().toISOString(),
    status: 'pending'
  });
  await refreshCount();
  return operationId;
}

export async function syncMutationQueue(): Promise<void> {
  if (syncInFlight || !navigator.onLine) return;
  syncInFlight = true;

  try {
    const mutations = await clientDb.mutations.orderBy('createdAt').toArray();
    for (const mutation of mutations) {
      await clientDb.mutations.update(mutation.operationId, { status: 'syncing', lastError: undefined });
      await refreshCount();

      try {
        const response = await fetch(mutation.url, {
          method: mutation.method,
          credentials: 'include',
          headers: {
            'content-type': 'application/json',
            'idempotency-key': mutation.operationId
          },
          body: JSON.stringify(mutation.body)
        });

        if (response.status === 401 || response.status === 403) {
          throw new Error('AUTHORIZATION_REQUIRED');
        }
        if (!response.ok) {
          throw new Error(`SYNC_HTTP_${response.status}`);
        }

        await clientDb.mutations.delete(mutation.operationId);
      } catch (error) {
        await clientDb.mutations.update(mutation.operationId, {
          status: 'failed',
          lastError: error instanceof Error ? error.message : 'SYNC_FAILED'
        });
        break;
      }
      await refreshCount();
    }
  } finally {
    syncInFlight = false;
    await refreshCount();
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void syncMutationQueue());
}
